// =============================================================================
// Centralized mock dataset. Every service reads from here so the demo data is
// internally consistent. Replace these with network calls when wiring the real
// backend — the service layer signatures are the contract.
// =============================================================================

import type {
  Application,
  AssessmentQuestion,
  BillingRecord,
  CareerGoal,
  Company,
  Connection,
  Education,
  Experience,
  Job,
  MessageThread,
  Occupation,
  OccupationCategory,
  Plan,
  Skill,
} from '../../types';

export const categories: OccupationCategory[] = [
  { id: 'tech', name: 'Technology', description: 'Software, data, and IT roles', occupationCount: 48, icon: 'Cpu', color: 'blue' },
  { id: 'design', name: 'Design', description: 'Product, UX, and visual design', occupationCount: 22, icon: 'Palette', color: 'pink' },
  { id: 'business', name: 'Business', description: 'Management, operations & strategy', occupationCount: 34, icon: 'Briefcase', color: 'amber' },
  { id: 'health', name: 'Healthcare', description: 'Clinical and allied health roles', occupationCount: 41, icon: 'HeartPulse', color: 'rose' },
  { id: 'finance', name: 'Finance', description: 'Accounting, banking & investment', occupationCount: 27, icon: 'Landmark', color: 'emerald' },
  { id: 'marketing', name: 'Marketing', description: 'Growth, content & brand', occupationCount: 19, icon: 'Megaphone', color: 'violet' },
];

export const occupations: Occupation[] = [
  {
    id: '1', title: 'Software Engineer', category: 'Technology', categoryId: 'tech',
    description: 'Design, build, and maintain software applications and systems.',
    salary: '$120,000', salaryMin: 95000, salaryMax: 165000, growth: '25%', growthRate: 25,
    skills: ['JavaScript', 'React', 'Node.js', 'System Design'], demand: 'very-high',
    education: "Bachelor's in CS or equivalent", trending: true, recommended: true, openings: 184000,
  },
  {
    id: '2', title: 'Data Scientist', category: 'Technology', categoryId: 'tech',
    description: 'Analyze complex data to drive organizational decisions.',
    salary: '$130,000', salaryMin: 105000, salaryMax: 180000, growth: '36%', growthRate: 36,
    skills: ['Python', 'SQL', 'Machine Learning', 'Statistics'], demand: 'very-high',
    education: "Master's preferred", trending: true, recommended: true, openings: 42000,
  },
  {
    id: '3', title: 'UX Designer', category: 'Design', categoryId: 'design',
    description: 'Craft intuitive, user-friendly interfaces and experiences.',
    salary: '$95,000', salaryMin: 72000, salaryMax: 135000, growth: '15%', growthRate: 15,
    skills: ['Figma', 'User Research', 'Prototyping', 'Design Systems'], demand: 'high',
    education: "Bachelor's in Design or HCI", trending: false, recommended: true, openings: 23000,
  },
  {
    id: '4', title: 'Product Manager', category: 'Business', categoryId: 'business',
    description: 'Own product strategy, roadmap, and cross-functional delivery.',
    salary: '$135,000', salaryMin: 110000, salaryMax: 190000, growth: '19%', growthRate: 19,
    skills: ['Roadmapping', 'Analytics', 'Stakeholder Mgmt', 'Agile'], demand: 'high',
    education: "Bachelor's + experience", trending: true, recommended: false, openings: 31000,
  },
  {
    id: '5', title: 'Registered Nurse', category: 'Healthcare', categoryId: 'health',
    description: 'Provide and coordinate patient care across settings.',
    salary: '$85,000', salaryMin: 65000, salaryMax: 110000, growth: '6%', growthRate: 6,
    skills: ['Patient Care', 'Clinical Assessment', 'EMR', 'Empathy'], demand: 'very-high',
    education: 'BSN + RN license', trending: false, recommended: false, openings: 195000,
  },
  {
    id: '6', title: 'Financial Analyst', category: 'Finance', categoryId: 'finance',
    description: 'Evaluate financial data to guide investment and budgeting.',
    salary: '$90,000', salaryMin: 70000, salaryMax: 125000, growth: '9%', growthRate: 9,
    skills: ['Excel', 'Financial Modeling', 'Forecasting', 'Valuation'], demand: 'medium',
    education: "Bachelor's in Finance", trending: false, recommended: false, openings: 28000,
  },
  {
    id: '7', title: 'Growth Marketer', category: 'Marketing', categoryId: 'marketing',
    description: 'Drive acquisition and retention through data-led experiments.',
    salary: '$98,000', salaryMin: 70000, salaryMax: 140000, growth: '22%', growthRate: 22,
    skills: ['SEO', 'Paid Ads', 'Analytics', 'A/B Testing'], demand: 'high',
    education: "Bachelor's preferred", trending: true, recommended: true, openings: 17000,
  },
  {
    id: '8', title: 'DevOps Engineer', category: 'Technology', categoryId: 'tech',
    description: 'Automate infrastructure, CI/CD, and reliability.',
    salary: '$128,000', salaryMin: 100000, salaryMax: 175000, growth: '21%', growthRate: 21,
    skills: ['Kubernetes', 'AWS', 'Terraform', 'CI/CD'], demand: 'very-high',
    education: "Bachelor's or equivalent", trending: true, recommended: false, openings: 36000,
  },
];

export const plans: Plan[] = [
  {
    id: 'starter', name: 'Starter', tagline: 'Explore your options', price: 0, interval: 'month',
    features: [
      { label: 'Browse all occupations', included: true },
      { label: '1 career assessment', included: true },
      { label: 'Basic salary insights', included: true },
      { label: 'AI recommendations', included: false },
      { label: 'Mentor matching', included: false },
      { label: 'Priority job alerts', included: false },
    ],
  },
  {
    id: 'premium', name: 'Premium', tagline: 'Accelerate your career', price: 29, interval: 'month',
    popular: true, freeTrialDays: 60,
    features: [
      { label: 'Everything in Starter', included: true },
      { label: 'Unlimited assessments', included: true, highlight: true },
      { label: 'AI-powered recommendations', included: true, highlight: true },
      { label: 'Personalized roadmaps', included: true },
      { label: 'Mentor matching', included: true },
      { label: 'Priority job alerts', included: false },
    ],
  },
  {
    id: 'pro', name: 'Pro', tagline: 'For serious switchers', price: 59, interval: 'month',
    features: [
      { label: 'Everything in Premium', included: true },
      { label: 'Priority job alerts', included: true, highlight: true },
      { label: '1:1 coaching sessions', included: true, highlight: true },
      { label: 'Resume & portfolio review', included: true },
      { label: 'Dedicated success manager', included: true },
      { label: 'Interview prep library', included: true },
    ],
  },
];

export const assessmentQuestions: AssessmentQuestion[] = [
  {
    id: 'q1', prompt: 'Which activity sounds most enjoyable?', category: 'interests',
    options: [
      { id: 'q1a', label: 'Building or fixing things', weight: { tech: 3, health: 1 } },
      { id: 'q1b', label: 'Designing something beautiful', weight: { design: 3, marketing: 1 } },
      { id: 'q1c', label: 'Leading a team to a goal', weight: { business: 3, finance: 1 } },
      { id: 'q1d', label: 'Helping people directly', weight: { health: 3, business: 1 } },
    ],
  },
  {
    id: 'q2', prompt: 'How do you prefer to solve problems?', category: 'style',
    options: [
      { id: 'q2a', label: 'With data and logic', weight: { tech: 2, finance: 2 } },
      { id: 'q2b', label: 'With creativity and intuition', weight: { design: 2, marketing: 2 } },
      { id: 'q2c', label: 'By collaborating with others', weight: { business: 2, health: 1 } },
      { id: 'q2d', label: 'Through careful research', weight: { finance: 2, tech: 1 } },
    ],
  },
  {
    id: 'q3', prompt: 'What work environment energizes you?', category: 'environment',
    options: [
      { id: 'q3a', label: 'Fast-paced startup', weight: { tech: 2, marketing: 2 } },
      { id: 'q3b', label: 'Structured enterprise', weight: { finance: 2, business: 1 } },
      { id: 'q3c', label: 'Creative studio', weight: { design: 3 } },
      { id: 'q3d', label: 'Mission-driven org', weight: { health: 3 } },
    ],
  },
  {
    id: 'q4', prompt: 'Which skill would you most like to grow?', category: 'growth',
    options: [
      { id: 'q4a', label: 'Technical / coding', weight: { tech: 3 } },
      { id: 'q4b', label: 'Visual / design', weight: { design: 3 } },
      { id: 'q4c', label: 'Leadership / strategy', weight: { business: 3 } },
      { id: 'q4d', label: 'Communication / influence', weight: { marketing: 3 } },
    ],
  },
  {
    id: 'q5', prompt: 'What matters most in your next role?', category: 'values',
    options: [
      { id: 'q5a', label: 'High earning potential', weight: { finance: 2, tech: 2 } },
      { id: 'q5b', label: 'Creative freedom', weight: { design: 3 } },
      { id: 'q5c', label: 'Job stability', weight: { health: 2, finance: 1 } },
      { id: 'q5d', label: 'Rapid advancement', weight: { business: 2, marketing: 1 } },
    ],
  },
];

export const careerGoals: CareerGoal[] = [
  { id: 'g1', title: 'Land a new job', timeframe: '6 months' },
  { id: 'g2', title: 'Switch industries', timeframe: '1 year' },
  { id: 'g3', title: 'Get a promotion', timeframe: '1 year' },
  { id: 'g4', title: 'Learn in-demand skills', timeframe: '3 months' },
  { id: 'g5', title: 'Increase my salary', timeframe: '6 months' },
  { id: 'g6', title: 'Build a professional network', timeframe: '3 months' },
];

export const billingHistory: BillingRecord[] = [
  { id: 'b1', date: '2026-06-01', description: 'Premium Plan — Monthly', amount: 29, status: 'paid', invoiceUrl: '#' },
  { id: 'b2', date: '2026-05-01', description: 'Premium Plan — Monthly', amount: 29, status: 'paid', invoiceUrl: '#' },
  { id: 'b3', date: '2026-04-01', description: 'Premium Plan — Monthly', amount: 29, status: 'paid', invoiceUrl: '#' },
  { id: 'b4', date: '2026-03-01', description: 'Free Trial — Premium', amount: 0, status: 'paid' },
];

export const jobs: Job[] = [
  {
    id: 'j1', title: 'Senior Frontend Engineer', company: 'Northwind Labs', companyId: 'c1',
    location: 'San Francisco, CA', remote: true, type: 'Full-time', salaryMin: 140000, salaryMax: 185000,
    postedAt: '2026-06-12', description: 'Build delightful product experiences with React and TypeScript.',
    requirements: ['5+ years frontend', 'React + TypeScript', 'Design systems experience'],
    skills: ['React', 'TypeScript', 'CSS', 'Testing'], category: 'Technology', saved: true,
  },
  {
    id: 'j2', title: 'Product Designer', company: 'Acme Studio', companyId: 'c2',
    location: 'Remote', remote: true, type: 'Full-time', salaryMin: 95000, salaryMax: 130000,
    postedAt: '2026-06-10', description: 'Own end-to-end design for our flagship product.',
    requirements: ['Portfolio required', 'Figma mastery', 'User research'],
    skills: ['Figma', 'Prototyping', 'User Research'], category: 'Design',
  },
  {
    id: 'j3', title: 'Data Scientist', company: 'Quanta AI', companyId: 'c3',
    location: 'New York, NY', remote: false, type: 'Full-time', salaryMin: 130000, salaryMax: 175000,
    postedAt: '2026-06-08', description: 'Build ML models that power our recommendation engine.',
    requirements: ['Python', 'ML frameworks', 'PhD or equivalent'],
    skills: ['Python', 'PyTorch', 'SQL', 'Statistics'], category: 'Technology',
  },
  {
    id: 'j4', title: 'Growth Marketing Lead', company: 'Brightside', companyId: 'c4',
    location: 'Austin, TX', remote: true, type: 'Full-time', salaryMin: 110000, salaryMax: 150000,
    postedAt: '2026-06-05', description: 'Lead acquisition strategy across paid and organic channels.',
    requirements: ['5+ years growth', 'Paid acquisition', 'Analytics'],
    skills: ['SEO', 'Paid Ads', 'Analytics'], category: 'Marketing',
  },
  {
    id: 'j5', title: 'DevOps Engineer', company: 'Northwind Labs', companyId: 'c1',
    location: 'Remote', remote: true, type: 'Contract', salaryMin: 120000, salaryMax: 160000,
    postedAt: '2026-06-03', description: 'Own our Kubernetes infrastructure and CI/CD pipelines.',
    requirements: ['Kubernetes', 'AWS', 'Terraform'],
    skills: ['Kubernetes', 'AWS', 'Terraform', 'CI/CD'], category: 'Technology',
  },
  {
    id: 'j6', title: 'Financial Analyst', company: 'Meridian Capital', companyId: 'c5',
    location: 'Chicago, IL', remote: false, type: 'Full-time', salaryMin: 80000, salaryMax: 115000,
    postedAt: '2026-05-30', description: 'Support investment decisions with rigorous financial analysis.',
    requirements: ['Financial modeling', 'Excel', 'CFA a plus'],
    skills: ['Excel', 'Modeling', 'Valuation'], category: 'Finance',
  },
];

export const companies: Company[] = [
  { id: 'c1', name: 'Northwind Labs', industry: 'Technology', size: '501-1000', location: 'San Francisco, CA', description: 'Developer tools for modern teams.', openJobs: 12, rating: 4.6, following: true, logoColor: 'blue' },
  { id: 'c2', name: 'Acme Studio', industry: 'Design', size: '51-200', location: 'Remote', description: 'A design-led product studio.', openJobs: 5, rating: 4.8, logoColor: 'pink' },
  { id: 'c3', name: 'Quanta AI', industry: 'Artificial Intelligence', size: '201-500', location: 'New York, NY', description: 'AI infrastructure at scale.', openJobs: 8, rating: 4.4, logoColor: 'violet' },
  { id: 'c4', name: 'Brightside', industry: 'Consumer', size: '11-50', location: 'Austin, TX', description: 'Wellness products people love.', openJobs: 3, rating: 4.5, logoColor: 'amber' },
  { id: 'c5', name: 'Meridian Capital', industry: 'Finance', size: '1000+', location: 'Chicago, IL', description: 'Global investment management.', openJobs: 9, rating: 4.1, logoColor: 'emerald' },
];

export const applications: Application[] = [
  { id: 'a1', jobId: 'j1', jobTitle: 'Senior Frontend Engineer', company: 'Northwind Labs', appliedAt: '2026-06-13', status: 'interview', lastUpdate: '2026-06-15' },
  { id: 'a2', jobId: 'j3', jobTitle: 'Data Scientist', company: 'Quanta AI', appliedAt: '2026-06-09', status: 'screening', lastUpdate: '2026-06-12' },
  { id: 'a3', jobId: 'j4', jobTitle: 'Growth Marketing Lead', company: 'Brightside', appliedAt: '2026-06-06', status: 'applied', lastUpdate: '2026-06-06' },
  { id: 'a4', jobId: 'j6', jobTitle: 'Financial Analyst', company: 'Meridian Capital', appliedAt: '2026-05-31', status: 'rejected', lastUpdate: '2026-06-10' },
  { id: 'a5', jobId: 'j2', jobTitle: 'Product Designer', company: 'Acme Studio', appliedAt: '2026-06-14', status: 'offer', lastUpdate: '2026-06-16' },
];

export const connections: Connection[] = [
  { id: 'n1', name: 'Sarah Chen', title: 'Engineering Manager', company: 'Northwind Labs', mutualConnections: 12, status: 'connected', avatarColor: 'blue' },
  { id: 'n2', name: 'Marcus Lee', title: 'Senior Designer', company: 'Acme Studio', mutualConnections: 8, status: 'connected', avatarColor: 'pink' },
  { id: 'n3', name: 'Priya Patel', title: 'Data Lead', company: 'Quanta AI', mutualConnections: 5, status: 'pending', avatarColor: 'violet' },
  { id: 'n4', name: 'James Wright', title: 'VP Marketing', company: 'Brightside', mutualConnections: 3, status: 'suggested', avatarColor: 'amber' },
  { id: 'n5', name: 'Elena Rossi', title: 'Recruiter', company: 'Meridian Capital', mutualConnections: 21, status: 'suggested', avatarColor: 'emerald' },
];

export const skills: Skill[] = [
  { id: 's1', name: 'React', level: 90, endorsements: 24 },
  { id: 's2', name: 'TypeScript', level: 85, endorsements: 19 },
  { id: 's3', name: 'System Design', level: 70, endorsements: 11 },
  { id: 's4', name: 'Node.js', level: 75, endorsements: 14 },
  { id: 's5', name: 'UX Research', level: 55, endorsements: 6 },
];

export const experiences: Experience[] = [
  { id: 'e1', role: 'Frontend Engineer', company: 'Acme Studio', startDate: '2023-01', current: true, description: 'Lead frontend for the core product, shipping React features used by 50k+ users.' },
  { id: 'e2', role: 'Junior Developer', company: 'Brightside', startDate: '2021-03', endDate: '2022-12', description: 'Built and maintained marketing site and internal tools.' },
];

export const education: Education[] = [
  { id: 'ed1', school: 'State University', degree: "Bachelor's", field: 'Computer Science', startYear: '2017', endYear: '2021' },
];

export const messageThreads: MessageThread[] = [
  {
    userId: 'n1', name: 'Sarah Chen', title: 'Engineering Manager', avatarColor: 'blue',
    lastMessage: 'Happy to refer you — send your resume!', unread: 2,
    messages: [
      { id: 'm1', fromMe: false, text: 'Hi! Saw you applied to Northwind.', timestamp: '2026-06-15T09:00:00Z' },
      { id: 'm2', fromMe: true, text: 'Yes! Really excited about the role.', timestamp: '2026-06-15T09:05:00Z' },
      { id: 'm3', fromMe: false, text: 'Happy to refer you — send your resume!', timestamp: '2026-06-15T09:10:00Z' },
    ],
  },
  {
    userId: 'n2', name: 'Marcus Lee', title: 'Senior Designer', avatarColor: 'pink',
    lastMessage: 'Let’s grab coffee next week.', unread: 0,
    messages: [
      { id: 'm4', fromMe: false, text: 'Loved your portfolio update.', timestamp: '2026-06-14T14:00:00Z' },
      { id: 'm5', fromMe: true, text: 'Thanks Marcus! Means a lot.', timestamp: '2026-06-14T14:30:00Z' },
      { id: 'm6', fromMe: false, text: 'Let’s grab coffee next week.', timestamp: '2026-06-14T15:00:00Z' },
    ],
  },
];

// Chart-friendly sample series ------------------------------------------------

export const salaryTrendData = [
  { year: '2021', salary: 92000 },
  { year: '2022', salary: 101000 },
  { year: '2023', salary: 110000 },
  { year: '2024', salary: 118000 },
  { year: '2025', salary: 126000 },
  { year: '2026', salary: 135000 },
];

export const demandByCategoryData = [
  { category: 'Tech', openings: 184 },
  { category: 'Health', openings: 195 },
  { category: 'Business', openings: 96 },
  { category: 'Finance', openings: 56 },
  { category: 'Design', openings: 46 },
  { category: 'Marketing', openings: 34 },
];

export const applicationFunnelData = [
  { stage: 'Saved', count: 18 },
  { stage: 'Applied', count: 12 },
  { stage: 'Screening', count: 6 },
  { stage: 'Interview', count: 3 },
  { stage: 'Offer', count: 1 },
];
